// src/pages/CompliancePage.tsx
//
// Top-level Compliance module. Tab-based layout:
//   Overview / MyRequirements / Documents / Expiring / Approvals /
//   Requirements / Categories
//
// The tab is reflected in ?tab=... so users can deep-link. Every tab
// component is workspace-scoped via useActiveWorkspace + gated by
// `compliance.view` on the module itself; per-action gating lives in
// each tab.

import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useHasCapability } from '../services/centralContext';
import { useComplianceRealtime } from '../hooks/useComplianceRealtime';
import ErrorState from '../components/common/ErrorState';
import OverviewTab from '../components/compliance/OverviewTab';
import MyRequirementsTab from '../components/compliance/MyRequirementsTab';
import DocumentsTab from '../components/compliance/DocumentsTab';
import ExpiringTab from '../components/compliance/ExpiringTab';
import ApprovalsTab from '../components/compliance/ApprovalsTab';
import ReviewsTab from '../components/compliance/ReviewsTab';
import RequirementsTab from '../components/compliance/RequirementsTab';
import CategoriesTab from '../components/compliance/CategoriesTab';

type TabKey =
  | 'overview'
  | 'my'
  | 'documents'
  | 'expiring'
  | 'approvals'
  | 'reviews'
  | 'requirements'
  | 'categories';

interface TabDef {
  key: TabKey;
  label: string;
}

// Ordered as they appear in the top-of-page nav.
const TABS: readonly TabDef[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'my', label: 'My Requirements' },
  { key: 'documents', label: 'Documents' },
  { key: 'expiring', label: 'Expiring' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'requirements', label: 'Requirements' },
  { key: 'categories', label: 'Categories' },
] as const;

const KNOWN_TABS = new Set<TabKey>(TABS.map((t) => t.key));

function isTabKey(v: string | null): v is TabKey {
  return !!v && KNOWN_TABS.has(v as TabKey);
}

export default function CompliancePage() {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;
  const canView = useHasCapability('compliance', 'view');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeTab: TabKey = useMemo(() => {
    const t = searchParams.get('tab');
    return isTabKey(t) ? t : 'overview';
  }, [searchParams]);

  useComplianceRealtime({ workspaceId });

  if (!canView) {
    return (
      <div className="p-6">
        <ErrorState
          title="Compliance is not enabled for you"
          message="Ask your workspace admin to grant the compliance.view capability."
        />
      </div>
    );
  }
  if (!workspaceId) {
    return (
      <div className="p-6">
        <ErrorState
          title="No workspace selected"
          message="Select a workspace to view compliance."
        />
      </div>
    );
  }

  const setTab = (key: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="compliance-page">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Compliance</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track requirements, evidence, approvals, and expirations across your workspace.
          </p>
        </div>
      </header>

      <div
        role="tablist"
        aria-label="Compliance sections"
        className="border-b border-gray-200 overflow-x-auto"
      >
        <div className="flex gap-4 md:gap-6 min-w-max">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              role="tab"
              type="button"
              aria-selected={activeTab === tab.key}
              onClick={() => setTab(tab.key)}
              data-testid={`compliance-tab-${tab.key}`}
              className={`pb-3 -mb-px text-sm font-medium border-b-2 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-t whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div role="tabpanel" aria-label={`Compliance ${activeTab}`}>
        {activeTab === 'overview' && (
          <OverviewTab workspaceId={workspaceId} onNavigateTab={setTab} />
        )}
        {activeTab === 'my' && (
          <MyRequirementsTab workspaceId={workspaceId} />
        )}
        {activeTab === 'documents' && (
          <DocumentsTab workspaceId={workspaceId} />
        )}
        {activeTab === 'expiring' && (
          <ExpiringTab workspaceId={workspaceId} />
        )}
        {activeTab === 'approvals' && (
          <ApprovalsTab workspaceId={workspaceId} />
        )}
        {activeTab === 'reviews' && (
          <ReviewsTab workspaceId={workspaceId} />
        )}
        {activeTab === 'requirements' && (
          <RequirementsTab workspaceId={workspaceId} onNavigate={navigate} />
        )}
        {activeTab === 'categories' && (
          <CategoriesTab workspaceId={workspaceId} />
        )}
      </div>
    </div>
  );
}
